import { Button, Modal, Badge, Title, Stack } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import { primaryColor } from '../../../../constants/theme';
import { useMemo, useState, useCallback } from 'react';
import { useApp } from '../../../../context/AppContext';
import { ErrorModal } from '../error/ErrorModal';
import {
  createPaymentAccount,
  getAccountSession,
  getOnboardingStatus,
  getPublishableKey,
} from '../../../../services/PaymentService';
import {
  loadConnectAndInitialize,
  StripeConnectInstance,
} from '@stripe/connect-js';
import {
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
} from '@stripe/react-connect-js';

export const StripeStatusButton = ({
  width,
  isPrimary,
}: {
  width?: number;
  isPrimary?: boolean;
}) => {
  const { profile, token, onSetProfile } = useApp();
  const [stripeConnectInstance, setStripeConnectInstance] =
    useState<StripeConnectInstance>();
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const paymentStatus = useMemo(() => {
    if (
      profile &&
      'paymentAccountDetailsSubmitted' in profile &&
      profile.paymentAccountDetailsSubmitted &&
      'paymentAccountChargesEnabled' in profile &&
      profile.paymentAccountChargesEnabled &&
      'paymentAccountPayoutsEnabled' in profile &&
      profile.paymentAccountPayoutsEnabled
    ) {
      return 'Verified';
    } else if (
      profile &&
      'detailsSubmitted' in profile &&
      profile.detailsSubmitted
    ) {
      return 'Processing';
    } else {
      return 'None';
    }
  }, [profile]);

  const handleStripeButtonClick = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('payment status is', paymentStatus);
      if (paymentStatus === 'None') {
        if (profile) {
          if (!profile.paymentAccountId) {
            profile.paymentAccountId = (
              await createPaymentAccount(token)
            ).accountId;
          }

          const publishableKey = await getPublishableKey(token);
          const stripeConnectInstance = await loadConnectAndInitialize({
            publishableKey,
            fetchClientSecret: async () => {
              const { clientSecret } = await getAccountSession(token);
              return clientSecret;
            },
          });
          setStripeConnectInstance(stripeConnectInstance);
          setOnboardingModalOpen(true);
        }
      } else if (paymentStatus === 'Processing') {
        const onboardingStatus = await getOnboardingStatus(token);
        if (profile) {
          profile.paymentAccountDetailsSubmitted =
            onboardingStatus.detailsSubmitted;
          profile.paymentAccountChargesEnabled =
            onboardingStatus.chargesEnabled;
          profile.paymentAccountPayoutsEnabled =
            onboardingStatus.payoutsEnabled;
          onSetProfile({ ...profile });
        }
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error handling Stripe button click:', error);
      setIsLoading(false);
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Failed to initialize Stripe. Please try again.'
      );
      setIsError(true);
    }
  }, [paymentStatus, profile, token, onSetProfile]);

  const buttonText = useMemo(() => {
    return paymentStatus === 'Processing'
      ? 'Complete Stripe Onboarding'
      : !profile?.paymentAccountId
        ? 'Create Payment Account'
        : 'Refresh Stripe Status';
  }, [paymentStatus, profile?.paymentAccountId]);

  const onModalClose = useCallback(async () => {
    setOnboardingModalOpen(false);
    const onboardingStatus = await getOnboardingStatus(token);
    if (profile) {
      profile.paymentAccountDetailsSubmitted =
        onboardingStatus.detailsSubmitted;
      profile.paymentAccountChargesEnabled = onboardingStatus.chargesEnabled;
      profile.paymentAccountPayoutsEnabled = onboardingStatus.payoutsEnabled;
      onSetProfile({ ...profile });
    }
  }, [token, profile, onSetProfile]);

  const handleErrorDismiss = useCallback(() => {
    setIsError(false);
    setErrorMessage('');
  }, []);

  if (!profile?.isProfessional) return null;

  return (
    <>
      <ErrorModal
        isInError={isError}
        errorMessage={errorMessage}
        dismissAction={handleErrorDismiss}
        retryAction={handleStripeButtonClick}
        isLoading={isLoading}
      />
      <StripeOnboardingModal
        stripeConnectInstance={stripeConnectInstance}
        opened={onboardingModalOpen}
        onClose={onModalClose}
      />
      {paymentStatus === 'Verified' ? (
        <Badge
          w={width}
          fw={800}
          h={36}
          size="md"
          color={primaryColor}
          variant="outline"
          radius={'sm'}
          leftSection={<IconCircleCheck size={20} />}
        >
          Stripe Verified
        </Badge>
      ) : (
        <Button
          w={width}
          variant={isPrimary ? 'filled' : 'outline'}
          color={primaryColor}
          onClick={handleStripeButtonClick}
          loading={isLoading}
        >
          {buttonText}
        </Button>
      )}
    </>
  );
};

export interface StripeOnboardingModalProps {
  stripeConnectInstance: StripeConnectInstance | undefined;
  opened: boolean;
  onClose: () => void;
}

export const StripeOnboardingModal = ({
  stripeConnectInstance,
  opened,
  onClose,
}: StripeOnboardingModalProps) => {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      closeOnClickOutside={false}
      withCloseButton={true}
    >
      <Stack gap="lg">
        <Title order={3} fw="normal">
          Complete Stripe Onboarding
        </Title>
        {stripeConnectInstance && (
          <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
            <ConnectAccountOnboarding onExit={onClose} />
          </ConnectComponentsProvider>
        )}
      </Stack>
    </Modal>
  );
};
